<script lang="ts">
import {defineComponent} from 'vue';
import Icon from "../Icon.vue";

export default defineComponent({
  name: "VideoSection",
  components: {Icon},
  props: {
    video: String,
    title: String,
    subtitle: String,
    autoplay: {
      type: Boolean,
      default: false
    },
    muted: {
      type: Boolean,
      default: false
    },
    preview: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    videoUrl(): string {
      if (!this.video) return '';

      let id: string | null = null;

      if (!this.video.includes('http')) {
        id = this.video;
      } else {
        id = this.extractYouTubeVideoId(this.video);
      }

      if (!id) return '';

      const params = new URLSearchParams({
        autoplay: this.autoplay ? '1' : '0',
        mute: (this.muted || this.autoplay) ? '1' : '0'
      });

      return `https://www.youtube.com/embed/${id}?${params.toString()}`;
    }
  },
  methods: {
    extractYouTubeVideoId(url: string): string | null {
      try {
        const parsedUrl = new URL(url);

        if (parsedUrl.hostname === 'youtu.be') {
          return parsedUrl.pathname.slice(1);
        }

        if (parsedUrl.hostname.endsWith('youtube.com')) {
          if (parsedUrl.searchParams.has('v')) {
            return parsedUrl.searchParams.get('v');
          }

          const pathMatch = parsedUrl.pathname.match(/^\/(?:embed|live)\/([^/?]+)/);
          if (pathMatch) return pathMatch[1];
        }

        return null;
      } catch {
        return null;
      }
    }
  }
});
</script>

<template>
  <div class="outer-container">
    <div class="inner-container">
      <h2
        v-if="title"
        class="section-title"
      >
        {{ title }}
      </h2>
      <p
        v-if="subtitle"
        class="section-subtitle"
      >
        {{ subtitle }}
      </p>
      <div
        v-if="!preview && videoUrl"
        class="video"
      >
        <iframe
          :src="videoUrl"
          allowfullscreen
          title="YouTube video"
        />
      </div>
      <div
        v-else
        class="video video-preview"
      >
        <icon
          :icon="'youtube-brands-solid-full'"
          :size="80"
          :color="'#ffffff'"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.inner-container {
  text-align: center;
}

.section-title {
  font-size: $section-title-size;
  font-weight: $section-title-weight;
  line-height: $section-title-line;
  word-break: break-word;
  margin-bottom: $space-xs;
}

.section-subtitle {
  font-size: $section-body-size;
  font-weight: $section-body-weight;
  line-height: $section-body-line;
  color: $section-body-color;
  word-break: break-word;
}

.video {
  width: 70%;
  aspect-ratio: 16 / 9;
  margin: $space-2xl auto 0;
  border-radius: $radius-rounded;
  overflow: hidden;
  box-shadow: $shadow-card;
}

.video iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.video-preview {
  background-color: $color-neutral-900;
  display: flex;
  justify-content: center;
  align-items: center;
}

@media screen and (max-width: $breakpoint-max-sm) {
  .video {
    width: 100%;
  }
}

@container (min-width: 0px) and (max-width: 639px) {
  .video {
    width: 100% !important;
  }
}
</style>
